import React, { useMemo, useState } from 'react';
import { CheckCircle2, ChevronDown } from 'lucide-react';

const MAX_FAQ = 8;
const MIN_FAQ = 4;

const safeArray = (value) => (Array.isArray(value) ? value : []);

const buildComparisonRows = (comparisons) => {
  if (!comparisons || !Array.isArray(comparisons.rows)) {
    return [];
  }
  return comparisons.rows;
};

const AnswerFirstContent = ({
  quickAnswer,
  stats,
  comparisons,
  faqs,
  children,
  pageType,
  lastVerified,
}) => {
  const [openFaq, setOpenFaq] = useState(null);
  const statItems = safeArray(stats).slice(0, 3);
  const comparisonRows = buildComparisonRows(comparisons);
  const faqItems = safeArray(faqs).slice(0, MAX_FAQ);

  const validFaqCount = faqItems.filter((faq) => faq?.q && faq?.a).length;

  const lastVerifiedLabel = useMemo(() => {
    if (!lastVerified) {
      return 'Recently verified';
    }
    return lastVerified;
  }, [lastVerified]);

  return (
    <div className="space-y-6">
      <section
        className="terminal-grid p-5"
        style={{ background: 'rgba(255, 232, 0, 0.06)', borderLeft: '4px solid var(--accent-primary)' }}
        data-testid="quick-answer-block"
      >
        <h2 className="text-xs uppercase tracking-[0.16em] mb-2" style={{ color: 'var(--text-muted)' }}>
          Quick answer
        </h2>
        <p className="text-sm sm:text-base leading-relaxed" style={{ color: 'var(--text-primary)' }}>
          {quickAnswer}
        </p>
        <p className="mt-3 text-[11px] inline-flex items-center gap-1" style={{ color: 'var(--text-muted)' }}>
          <CheckCircle2 size={12} style={{ color: 'var(--accent-secondary)' }} />
          Last verified: {lastVerifiedLabel} · Updated when tools change pricing or features
        </p>
      </section>

      <section className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="answer-first-stat-row">
        {statItems.map((item, index) => (
          <div key={`${item.label}-${index}`} className="terminal-grid p-4">
            <p className="text-2xl font-semibold" style={{ color: 'var(--accent-primary)', fontFamily: 'JetBrains Mono, monospace' }}>
              {item.value}
            </p>
            <p className="text-xs uppercase tracking-[0.12em]" style={{ color: 'var(--text-muted)' }}>
              {item.label}
            </p>
          </div>
        ))}
      </section>

      {comparisonRows.length > 0 && (
        <section className="terminal-grid p-5 overflow-x-auto" data-testid="answer-first-comparison-table">
          <h2 className="text-lg font-semibold mb-3" style={{ color: 'var(--text-primary)', fontFamily: 'Unbounded, sans-serif' }}>
            Comparison Summary
          </h2>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b" style={{ borderColor: 'var(--border-subtle)', color: 'var(--text-muted)' }}>
                {safeArray(comparisons.headers).map((header) => (
                  <th key={header} className="py-2 pr-3">{header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {comparisonRows.map((row, rowIndex) => (
                <tr key={`cmp-${rowIndex}`} className="border-b" style={{ borderColor: 'var(--border-subtle)' }}>
                  {safeArray(row).map((cell, cellIndex) => (
                    <td key={`cmp-${rowIndex}-${cellIndex}`} className="py-2 pr-3" style={{ color: 'var(--text-secondary)' }}>
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      <section data-page-type={pageType}>{children}</section>

      <section className="terminal-grid p-5" data-testid="faq-section">
        <h2 className="text-lg font-semibold mb-3" style={{ color: 'var(--text-primary)', fontFamily: 'Unbounded, sans-serif' }}>
          Frequently Asked Questions
        </h2>
        {validFaqCount < MIN_FAQ && (
          <p className="text-xs mb-3" style={{ color: 'var(--text-muted)' }}>
            Add at least {MIN_FAQ} FAQ items for stronger answer engine coverage.
          </p>
        )}
        <div className="space-y-2">
          {faqItems.map((faq, index) => (
            <div key={`${faq.q}-${index}`} className="border" style={{ borderColor: 'var(--border-subtle)' }}>
              <button
                type="button"
                className="w-full px-4 py-3 flex items-center justify-between text-left"
                onClick={() => setOpenFaq((prev) => (prev === index ? null : index))}
                style={{ color: 'var(--text-primary)' }}
              >
                <span>{faq.q}</span>
                <ChevronDown
                  size={16}
                  style={{
                    color: 'var(--accent-primary)',
                    transform: openFaq === index ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease',
                  }}
                />
              </button>
              {openFaq === index && (
                <div className="px-4 pb-3 text-sm" style={{ color: 'var(--text-secondary)' }}>
                  {faq.a}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default AnswerFirstContent;
